import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BodyBadge } from '@/components/BodyBadge';
import { Icon } from '@/components/Icon';
import { fetchSignalMatches } from '@/lib/api';
import './SignalHistory.css';

/**
 * A Signal's full match history — all-time, not just today's feed.
 *
 * Opened by clicking the count badge on a Signal chip. Full-screen overlay rather than a
 * route: it is a lens on the feed, and the user should land back exactly where they were.
 */
export function SignalHistory({
  signalName,
  total,
  onClose,
}: {
  signalName: string;
  total: number;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['signal-history', signalName],
    queryFn: () => fetchSignalMatches(signalName),
  });

  // Escape closes, and focus moves into the overlay so a keyboard user is not stranded
  // behind it on the page underneath.
  useEffect(() => {
    closeRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = data ?? [];

  return (
    <div
      className="history"
      role="dialog"
      aria-modal="true"
      aria-label={`Match history for ${signalName}`}
      ref={dialogRef}
    >
      <header className="history__head">
        <div>
          <p className="u-meta">Full match history</p>
          <h2 className="history__title">{signalName}</h2>
          <p className="history__count">
            {total} {total === 1 ? 'match' : 'matches'} all time
          </p>
        </div>
        <button ref={closeRef} type="button" className="history__close" onClick={onClose}>
          <Icon name="chevron-left" /> Close
        </button>
      </header>

      <div className="history__rows">
        {isLoading && <p className="history__empty">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <p className="history__empty">This Signal hasn’t matched anything yet.</p>
        )}
        {rows.map((match) => (
          <article key={match.id} className="history__row">
            <div className="history__rowHead">
              <BodyBadge body={match.body} short />
              <strong>
                {match.city}, {match.region}
              </strong>
              <span className="history__date">{match.date}</span>
            </div>
            <p className="history__quote">
              {match.passage.before}
              <mark className="feed__mark">{match.passage.hit}</mark>
              {match.passage.after}
            </p>
            {match.num && <span className="history__num">{match.num}</span>}
          </article>
        ))}
      </div>
    </div>
  );
}
